import { Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  useFinanceBulkImportTemplateColumnsQuery,
  useStartFinanceBulkImportTemplateReportMutation,
} from './financeBulkImport.generated';
import { downloadCsv, serializeCsv } from './csv';

const COLUMN_HELP: Record<string, string> = {
  id: 'Store (str_…) or invoice collection (inc_…). Required.',
  entity_type: 'For orientation only; not imported.',
  name: 'For orientation only; not imported.',
  vat_number: 'Text.',
  invoice_iban_name: 'Text.',
  invoice_headline: 'Text; printed as the invoice headline.',
  regular_payment_target_days: 'Whole number of days, 1–365.',
  sepa_2_percent_discount: 'true or false.',
  inkassokennzeichen: 'true or false.',
  dunning_reminder_block_type: 'TEMPORARY, PERMANENTLY or empty.',
  dunning_reminder_block_reason: 'Text.',
  dunning_reminder_block_until: 'Date YYYY-MM-DD (DD.MM.YYYY also works).',
  invoice_address_country: 'Two-letter country code, e.g. DE.',
};

export function TemplateCard() {
  const { data, loading, error } = useFinanceBulkImportTemplateColumnsQuery();
  const [startReport, { loading: starting }] =
    useStartFinanceBulkImportTemplateReportMutation();
  const columns = data?.financeBulkImportTemplateColumns ?? [];

  const downloadEmptyTemplate = () =>
    downloadCsv('finance-bulk-import-template.csv', serializeCsv(columns, []));

  const requestTemplateWithValues = async () => {
    const now = new Date();
    try {
      await startReport({
        variables: { year: now.getFullYear(), month: now.getMonth() + 1 },
      });
      toast.success('Template report started', {
        description:
          'The download link arrives in the finance Slack channel (#fin-reports) in a few minutes.',
      });
    } catch (e) {
      toast.error('Could not start the template report', {
        description: (e as Error).message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>1. Get a template</CardTitle>
        <CardDescription>
          Fill in the rows to change and upload the file below. A column the
          file does not have is left unchanged, so you may delete columns you do
          not want to touch.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-wrap tw:gap-2">
          <Button
            variant="outline"
            disabled={columns.length === 0}
            onClick={downloadEmptyTemplate}
          >
            <Download />
            Download empty template
          </Button>
          <Button
            variant="outline"
            disabled={starting || columns.length === 0}
            onClick={requestTemplateWithValues}
          >
            {starting ? <Spinner /> : <FileSpreadsheet />}
            Request template with current values
          </Button>
        </div>
        {loading && <Spinner />}
        {error && (
          <p className="tw:text-destructive tw:text-sm">
            Could not load the template columns: {error.message}
          </p>
        )}
        {columns.length > 0 && (
          <div className="tw:grid tw:gap-x-6 tw:gap-y-1 tw:text-sm tw:sm:grid-cols-[max-content_1fr]">
            {columns.map((column) => (
              <div key={column} className="tw:contents">
                <code className="tw:text-xs">{column}</code>
                <span className="tw:text-muted-foreground">
                  {COLUMN_HELP[column] ?? ''}
                </span>
              </div>
            ))}
          </div>
        )}
        <ul className="tw:text-muted-foreground tw:list-disc tw:pl-5 tw:text-sm">
          <li>An empty cell clears the field (an empty boolean is false).</li>
          <li>
            Dunning block: TEMPORARY needs{' '}
            <code>dunning_reminder_block_until</code> (not in the past);
            PERMANENTLY needs <code>dunning_reminder_block_reason</code> and an
            empty until; an empty type needs both empty.
          </li>
          <li>
            Separator <code>;</code> or <code>,</code>. At most 1000 rows per
            import.
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}
